<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, onMounted, computed, watch } from 'vue';
  import { useStore } from 'vuex';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  import Device from '@/api/device';
  import Users from '@/api/users';
  import Entities from '@/api/entities';
  import Roles from '@/api/roles';
  import InfoIcon from '@/components/common/InfoIcon.vue';
  import { TrashIcon } from '@heroicons/vue/24/solid';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

  // Definitions
  const props = defineProps({
    entityDetails: {
      type: Object,
      default: () => {}
    },
    /** Data document IDs to pre-select in the device step (e.g. from dashboard unit card) */
    initialDeviceIds: {
      type: Array,
      default: () => []
    }
  });

  const store = useStore();
  const user = computed(() => store.state.user);

  const emit = defineEmits(['entity-details', 'close', 'entity-created']);

  const loading = ref(false);
  const loadingMessage = ref('');
  const grabbingDevices = ref(true);
  const devices = ref([]);
  const selectedDevices = ref([]);

  // Step 2 search + sort
  const searchQuery = ref('');
  const sortColumn = ref(null);
  const sortDirection = ref('asc');

  const filteredSortedDevices = computed(() => {
    let result = devices.value;
    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase();
      result = result.filter(d =>
        (d.name ?? '').toLowerCase().includes(q) || (d.id ?? '').toLowerCase().includes(q)
      );
    }
    if (sortColumn.value) {
      result = [...result].sort((a, b) => {
        const av = a[sortColumn.value];
        const bv = b[sortColumn.value];
        const an = parseFloat(av);
        const bn = parseFloat(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av ?? '').localeCompare(String(bv ?? ''));
        return sortDirection.value === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  });

  const toggleSort = (column) => {
    if (sortColumn.value === column) {
      if (sortDirection.value === 'asc') {
        sortDirection.value = 'desc';
      } else {
        sortColumn.value = null;
        sortDirection.value = 'asc';
      }
    } else {
      sortColumn.value = column;
      sortDirection.value = 'asc';
    }
  };

  const toggleDevice = (deviceId) => {
    const idx = selectedDevices.value.indexOf(deviceId);
    if (idx === -1) selectedDevices.value.push(deviceId);
    else selectedDevices.value.splice(idx, 1);
  };
  const error = ref('');
  const users = ref([]);
  const firstName = ref('');
  const lastName = ref('');
  const email = ref('');
  const role = ref('admin');
  const availableRoles = ref([]);
  const name = ref(props.entityDetails?.name || '');
  const logoFile = ref(props.entityDetails?.logo || null);
  const iconFile = ref(props.entityDetails?.icon || null);
  const url = ref(props.entityDetails?.url || '');

  // Step wizard state
  const currentStep = ref(1);
  const canProceed = ref(false);
  const hasEntityDetails = computed(() => Object.keys(props.entityDetails || {}).length > 0);
  const totalSteps = hasEntityDetails.value ? 3 : 4;
  const steps = ref({});
  
  // Initialize steps based on whether we're editing or creating
  const initializeSteps = () => {
    if (hasEntityDetails.value) {
      steps.value = {
        1: {
          title: "Basic Info",
          completed: true
        },
        2: {
          title: "Select Devices",
          completed: true
        },
        3: {
          title: "Review",
          completed: false
        }
      };
    } else {
      steps.value = {
        1: {
          title: "Basic Info",
          completed: false
        },
        2: {
          title: "Select Devices",
          completed: false
        },
        3: {
          title: "Invite Users",
          completed: false
        },
        4: {
          title: "Review",
          completed: false
        }
      };
    }
  };
  
  // Initialize steps on component mount
  initializeSteps();

  // Lifecycle hooks
  onMounted(async() => {
    // Get all devices for the user entity
    await getDevices();
    const rolesResponse = await Roles.get();
    if (rolesResponse.roles) {
      availableRoles.value = rolesResponse.roles;
      role.value = rolesResponse.roles[0]?.id ?? 'admin';
    }
    }
  );

  // Methods
  const getDevices = async() => {
    devices.value = [];
    const response = await Device.get('/all?entity=' + user.value.entity);
    if (response.status === 'success') {
      for (const device of response.details) {
        devices.value.push({
          id: device.id,
          name: device.friendlyName,
        });
      }
    }

    // Pre-select devices for the new inline table (uses IDs, not indexes)
    if (hasEntityDetails.value) {
      selectedDevices.value = response.details
        .filter(device => device.entities && device.entities.includes(props.entityDetails.id))
        .map(device => device.id);
    } else if (props.initialDeviceIds?.length && response.status === 'success') {
      const idSet = new Set(props.initialDeviceIds);
      selectedDevices.value = response.details
        .filter((device) => idSet.has(device.id))
        .map((device) => device.id);
    }
    grabbingDevices.value = false;
  }

  const isValidUrl = (urlString) => {
    if (!urlString || urlString.trim() === '') {
      return true; // Allow empty URLs
    }
    
    try {
      const url = new URL(urlString);
      return ['http:', 'https:', 'ftp:'].includes(url.protocol);
    } catch (error) {
      return false;
    }
  };

  const inviteUser = () => {
    users.value.push({
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      role: role.value
    });

    firstName.value = '';
    lastName.value = '';
    email.value = '';
    role.value = availableRoles.value[0]?.id ?? 'admin';
  }

  const removeUser = (index) => {
    users.value.splice(index, 1);
  }

  const setFile = (type, event) => {
    const file = event.target.files[0];
    if (type === 'logo') {
      logoFile.value = file;
    } else if (type === 'icon') {
      iconFile.value = file;
    }
  }

  const handleSubmit = async() => {
    loading.value = true;
    loadingMessage.value = 'Creating Entity...';
    error.value = '';

    // Validate URL if provided
    if (url.value && !isValidUrl(url.value)) {
      error.value = 'Please enter a valid URL (http, https, or ftp)';
      loading.value = false;
      return;
    }
    
    const formData = new FormData();
    formData.append('name', name.value);
    formData.append('logo', logoFile.value);
    formData.append('icon', iconFile.value);
    formData.append('url', url.value);
    
    const response = await Entities.post('', formData);
    const newEntityId = response.body?.details?.entityID;
    if (newEntityId) {
      const parentEntityData = user.value.entityData[user.value.entity] || {};
      const updatedSubEntities = [...(parentEntityData.subEntities || []), newEntityId];
      store.dispatch('setUser', {
        ...user.value,
        entityData: {
          ...user.value.entityData,
          [user.value.entity]: { ...parentEntityData, subEntities: updatedSubEntities },
          [newEntityId]: { name: name.value, subEntities: [] }
        }
      });
      if (!hasEntityDetails.value) {
        const toLink = selectedDevices.value.filter(Boolean);
        emit('entity-created', { entityId: newEntityId, name: name.value, deviceIds: toLink });
      }
    }
  
    if (users.value.length > 0) {
      loadingMessage.value = 'Inviting Users...';
      for (let user of users.value) {
        let responseData;

        const body = {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.role,
            entity: response.body.details.entityID,
        }

        try {
            responseData = await Users.post('', body);
        } catch (error) {
            console.error('Error creating entity user:', error);
        }
        setTimeout(() => {
            if (responseData.status === 'success') {
                // Success handled by UI
            } else {
                try {
                    alert(responseData.message);
                } catch (e) {
                    alert('Something went wrong when creating the user');
                }
            }
        }, 100);
      }
    }

    close();
  }

  const close = () => {
    loading.value = false;
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);

  // Step navigation methods
  const nextStep = () => {
    if (!canProceed.value) {
      error.value = 'Please fill in all the required fields.';
    }
    else if (currentStep.value < totalSteps) {
      steps.value[currentStep.value].completed = true;
      currentStep.value++;
      canProceed.value = false;
      error.value = '';
    }
  };

  const prevStep = () => {
    if (currentStep.value > 1) {
      currentStep.value--;
    }
  };

  const goToStep = (stepIndex) => {
    if (steps.value[stepIndex]?.completed || steps.value[stepIndex-1]?.completed) {
      currentStep.value = Number(stepIndex);
    }
  };

  // Step validation
  watch([currentStep, name, selectedDevices], () => {
    switch (currentStep.value) {
      case 1: // Basic Info
        if (name.value.trim() !== '') {
          canProceed.value = true;
        } else {
          steps.value[1].completed = false;
          canProceed.value = false;
        }
        break;
      case 2: // Select Devices
        steps.value[2].completed = true;
        canProceed.value = true;
        break;
      case 3: // Add Users (when creating) or Review (when editing)
        if (hasEntityDetails.value) {
          // Review step for edit mode
          steps.value[3].completed = true;
          canProceed.value = true;
        } else {
          // Add Users step for create mode - always can proceed
          steps.value[3].completed = true;
          canProceed.value = true;
        }
        break;
      case 4: // Review (when creating)
        steps.value[4].completed = true;
        canProceed.value = true;
        break;
    }
  }, { immediate: true });

</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal p-6 rounded-lg w-[700px] max-h-[calc(100vh-50px)] overflow-y-auto shadow-2xl" @mousedown="onContentMousedown">
      <!-- Header with step progress -->
      <div class="mb-6">
        <!-- Accent bar -->
        <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>

        <h2 v-if="!hasEntityDetails" class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Create A New Entity</h2>
        <h2 v-else class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Edit Entity</h2>

        <!-- Connected step progress -->
        <div class="flex items-center mb-5">
          <template v-for="(stepData, index) in steps" :key="index">
            <div class="flex flex-col items-center flex-shrink-0">
              <span
                class="text-[9px] font-bold uppercase tracking-widest mb-1.5 text-center w-14 leading-tight"
                :class="{
                  'text-mc-red': currentStep == Number(index),
                  'text-green-600': stepData.completed && currentStep != Number(index),
                  'text-ink-muted dark:text-on-dark-muted': !stepData.completed && currentStep != Number(index)
                }"
              >{{ stepData.title }}</span>
              <div
                class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer transition-all"
                :class="{
                  'bg-mc-red text-white ring-4 ring-mc-red/20': currentStep == Number(index),
                  'bg-green-600 text-white': stepData.completed && currentStep != Number(index),
                  'bg-mc-cloud border border-mc-steel text-ink-muted dark:bg-white/[0.07] dark:border-white/20 dark:text-on-dark-muted': !stepData.completed && currentStep != Number(index)
                }"
                @click="goToStep(Number(index))"
              >
                <span v-if="stepData.completed && currentStep != Number(index)">✓</span>
                <span v-else>{{ index }}</span>
              </div>
            </div>
            <!-- Connector line between steps (not after last) -->
            <div
              v-if="Number(index) < totalSteps"
              class="flex-1 h-[1.5px] mt-4 mx-1 transition-colors"
              :class="stepData.completed ? 'bg-green-600' : 'bg-mc-steel dark:bg-white/10'"
            ></div>
          </template>
        </div>

        <!-- Divider + step title -->
        <div class="h-px w-full bg-mc-steel dark:bg-line-on-dark mb-4"></div>
        <h3 class="text-sm font-bold text-center text-mc-ink dark:text-on-dark uppercase tracking-widest">
          {{ steps[currentStep].title }}
        </h3>
      </div>

      <form @submit.prevent="handleSubmit" class="min-h-[400px]">

        <!-- Step 1: Basic Information -->
        <div v-if="currentStep === 1" class="space-y-5">
          <div>
            <label for="entity-name" class="block text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5">
              Entity Name <span class="text-mc-red">*</span>
            </label>
            <input
              id="entity-name"
              v-model="name" type="text" required
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            />
            <p v-if="error && name.trim() === ''" class="text-mc-red text-xs mt-1">This field is required</p>
          </div>

          <div>
            <label for="entity-url" class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5">
              Entity URL
              <InfoIcon iconType="info" content="The URL must be a valid URL (http, https, or ftp) and must be accessible from the internet." />
            </label>
            <input
              id="entity-url"
              v-model="url" type="text"
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              placeholder="https://..."
            />
          </div>

          <div>
            <label for="entity-logo" class="block text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5">
              Logo
            </label>
            <input
              id="entity-logo"
              type="file" accept="image/*" @change="setFile('logo', $event)"
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            />
          </div>

          <div>
            <label for="entity-icon" class="block text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5">
              Icon
            </label>
            <input
              id="entity-icon"
              type="file" accept="image/*" @change="setFile('icon', $event)"
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            />
          </div>
        </div>

        <!-- Step 2: Device Selection -->
        <div v-if="currentStep === 2">
          <p class="font-bold mb-3">
            Select the devices you want to allow this entity to access
          </p>

          <!-- Search bar -->
          <div class="relative mb-2">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name or ID..."
              class="w-full pl-8 pr-3 py-2 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20 text-sm"
            />
            <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">⌕</span>
          </div>

          <!-- Sortable device table -->
          <div class="border border-mc-steel dark:border-line-on-dark rounded-mc-sm h-[260px] overflow-y-auto">
            <table class="w-full border-collapse">
              <thead class="bg-mc-cloud dark:bg-mc-charcoal sticky top-0">
                <tr>
                  <th class="w-8 p-2"></th>
                  <th
                    class="text-left p-2 text-xs uppercase tracking-wide cursor-pointer select-none"
                    :class="sortColumn === 'id' ? 'text-mc-red' : 'text-gray-500 dark:text-gray-400'"
                    @click="toggleSort('id')"
                  >
                    ID
                    <span>{{ sortColumn === 'id' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                  </th>
                  <th
                    class="text-left p-2 text-xs uppercase tracking-wide cursor-pointer select-none"
                    :class="sortColumn === 'name' ? 'text-mc-red' : 'text-gray-500 dark:text-gray-400'"
                    @click="toggleSort('name')"
                  >
                    Name
                    <span>{{ sortColumn === 'name' ? (sortDirection === 'asc' ? '↑' : '↓') : '↕' }}</span>
                  </th>
                </tr>
              </thead>
              <tbody v-if="!grabbingDevices">
                <tr
                  v-for="device in filteredSortedDevices"
                  :key="device.id"
                  class="border-t border-mc-steel/40 dark:border-line-on-dark hover:bg-mc-cloud dark:hover:bg-mc-charcoal/60 cursor-pointer"
                  :class="selectedDevices.includes(device.id) ? 'bg-mc-red-100 dark:bg-red-900/10' : ''"
                  @click="toggleDevice(device.id)"
                >
                  <td class="p-2 text-center">
                    <input
                      type="checkbox"
                      :checked="selectedDevices.includes(device.id)"
                      @click.stop
                      @change="toggleDevice(device.id)"
                    />
                  </td>
                  <td class="p-2 text-sm text-gray-500 dark:text-gray-400">{{ device.id }}</td>
                  <td class="p-2 text-sm">{{ device.name }}</td>
                </tr>
                <tr v-if="filteredSortedDevices.length === 0">
                  <td colspan="3" class="p-4 text-center text-sm text-gray-400">
                    No devices match your search.
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="3" class="p-4 text-center text-sm text-gray-400">
                    Loading devices...
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Footer -->
          <div class="flex justify-between mt-2 text-xs text-gray-500">
            <span>Showing {{ filteredSortedDevices.length }} of {{ devices.length }}</span>
            <span>Selected: {{ selectedDevices.length }} device(s)</span>
          </div>
        </div>

        <!-- Step 3: Add Users (only when not entityDetails)-->
        <div v-if="currentStep === 3 && !hasEntityDetails" class="space-y-5">
          <p class="text-xs text-ink-muted dark:text-on-dark-muted">
            You can always invite users later.
          </p>

          <!-- 2-col name row -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="invite-first-name" class="block text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5">First Name</label>
              <input
                id="invite-first-name"
                v-model="firstName" type="text"
                class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              />
            </div>
            <div>
              <label for="invite-last-name" class="block text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5">Last Name</label>
              <input
                id="invite-last-name"
                v-model="lastName" type="text"
                class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
              />
            </div>
          </div>

          <div>
            <label for="invite-email" class="block text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5">Email</label>
            <input
              id="invite-email"
              v-model="email" type="text"
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            />
          </div>

          <div>
            <label for="invite-role" class="block text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5">Role</label>
            <select
              id="invite-role"
              v-model="role"
              class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            >
              <option v-for="r in availableRoles" :key="r.id" :value="r.id">
                {{ r.id.replace(/-/g, ' ') }}
              </option>
            </select>
          </div>

          <div class="flex justify-center">
            <button
              type="button"
              @click="inviteUser"
              class="default-button"
              :disabled="!firstName.trim() || !email.trim()"
              :class="{ 'default-button-off': !firstName.trim() || !email.trim() }"
            >
              + Invite User
            </button>
          </div>

          <!-- Invited users list -->
          <div v-if="users.length > 0" class="space-y-2">
            <p class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted">Invited Users</p>
            <div
              v-for="(user, index) in users" :key="index"
              class="flex items-center justify-between p-3 border border-mc-steel dark:border-line-on-dark rounded-mc-sm bg-mc-cloud dark:bg-white/[0.04]"
            >
              <span class="text-sm text-mc-ink dark:text-on-dark">{{ user.firstName }} {{ user.lastName }} — {{ user.email }}</span>
              <div class="flex items-center gap-2">
                <span
                  class="px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide"
                  :class="user.role === 'admin' ? 'bg-mc-red/10 text-mc-red' : 'bg-mc-blue/10 text-mc-blue'"
                >{{ user.role }}</span>
                <button
                  type="button"
                  @click="removeUser(index)"
                  class="text-mc-red hover:text-mc-red/70 p-1 rounded transition-colors"
                  title="Remove user"
                >
                  <TrashIcon class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3/4: Review -->
        <div v-if="(currentStep === 4 && !hasEntityDetails) || (currentStep === 3 && hasEntityDetails)" class="space-y-4">
          <div class="grid grid-cols-2 gap-3">
            <div class="p-3 bg-mc-cloud dark:bg-white/[0.04] border border-mc-steel dark:border-line-on-dark rounded-mc-sm">
              <p class="text-[9px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1">Name</p>
              <p class="font-bold text-sm text-mc-ink dark:text-on-dark">{{ name || '—' }}</p>
            </div>
            <div class="p-3 bg-mc-cloud dark:bg-white/[0.04] border border-mc-steel dark:border-line-on-dark rounded-mc-sm">
              <p class="text-[9px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1">URL</p>
              <p class="font-bold text-sm text-mc-ink dark:text-on-dark truncate">{{ url || 'None' }}</p>
            </div>
            <div class="p-3 bg-mc-cloud dark:bg-white/[0.04] border border-mc-steel dark:border-line-on-dark rounded-mc-sm">
              <p class="text-[9px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1">Devices</p>
              <p class="font-bold text-sm text-mc-ink dark:text-on-dark">{{ selectedDevices.length }} selected</p>
            </div>
            <div class="p-3 bg-mc-cloud dark:bg-white/[0.04] border border-mc-steel dark:border-line-on-dark rounded-mc-sm">
              <p class="text-[9px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1">Invited Users</p>
              <p class="font-bold text-sm text-mc-ink dark:text-on-dark">{{ users.length > 0 ? users.length + ' user' + (users.length > 1 ? 's' : '') : 'None' }}</p>
            </div>
          </div>

          <!-- Device detail list -->
          <div v-if="selectedDevices.length > 0">
            <p class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-2">Device Details</p>
            <ul class="space-y-1">
              <li
                v-for="device in selectedDevices" :key="device"
                class="flex items-center gap-2 text-sm text-mc-ink dark:text-on-dark"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-mc-red flex-shrink-0"></span>
                <span class="text-ink-muted dark:text-on-dark-muted text-xs font-mono">{{ device }}</span>
                <span>{{ devices.find(d => d.id === device)?.name || '(Not Named)' }}</span>
              </li>
            </ul>
          </div>

          <!-- Invited users detail list -->
          <div v-if="users.length > 0">
            <p class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-2">User Details</p>
            <ul class="space-y-1">
              <li
                v-for="(user, index) in users" :key="index"
                class="flex items-center justify-between text-sm"
              >
                <span class="text-mc-ink dark:text-on-dark">{{ user.firstName }} {{ user.lastName }} — {{ user.email }}</span>
                <span
                  class="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide"
                  :class="user.role === 'admin' ? 'bg-mc-red/10 text-mc-red' : 'bg-mc-blue/10 text-mc-blue'"
                >{{ user.role }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="error" class="mt-5">
          <p class="font-normal text-red-600 whitespace-pre-line bg-red-50 dark:bg-red-900/30 p-3 rounded-lg">{{ error }}</p>
        </div>

        <!-- Navigation Buttons -->
        <div class="flex justify-between items-center mt-8 pt-6 border-t border-mc-steel dark:border-line-on-dark">
          <button
            type="button" @click="close"
            class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160"
          >
            Cancel
          </button>

          <div class="flex gap-3">
            <button
              v-if="currentStep > 1" type="button" @click="prevStep"
              class="btn-ghost-dark px-5 py-2 text-xs"
            >
              ← Previous
            </button>

            <button
              v-if="currentStep < totalSteps" type="button" @click="nextStep"
              class="default-button"
            >
              Next →
            </button>

            <div v-else-if="currentStep === totalSteps" class="flex items-center">
              <LoadingAnimation3 v-if="loading" size="30px" :message="loadingMessage" />
              <div v-else>
                <button
                  v-if="hasEntityDetails" type="submit"
                  :disabled="loading || !steps[currentStep].completed"
                  class="default-button"
                  :class="{ 'default-button-off': loading || !steps[currentStep].completed }"
                >
                  Update Entity
                </button>
                <button
                  v-else type="submit"
                  :disabled="loading || !steps[currentStep].completed"
                  class="default-button"
                  :class="{ 'default-button-off': loading || !steps[currentStep].completed }"
                >
                  Create Entity
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div id="preview">

    </div>
  </div>
</template>
